import { useMemo, useState } from 'react'
import { Search, X } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { CustomFilterIcon } from '@/components/ui/custom-filter-icon'
import { Input } from '@/components/ui/input'
import { useLanguage } from '@/contexts/language-context'
import { cn } from '@/lib/utils'
import { testId } from '@/lib/utils/test-id'

export interface FilterPaneProps {
  // Search now lives in the heatmap header next to the Worker label.
  workerTypeOptions: string[]
  jobFunctionOptions: string[]
  skillOptions: string[]
  certificationOptions: string[]
  /** ANY-match, same as Skills/Certifications: a worker matches if any of their skills is
   *  held at one of the selected levels. Not an "at this level or above" filter. */
  proficiencyLevelOptions: string[]
  selectedWorkerTypes: string[]
  selectedJobFunctions: string[]
  selectedSkills: string[]
  selectedCertifications: string[]
  selectedProficiencyLevels: string[]
  onWorkerTypesChange: (next: string[]) => void
  onJobFunctionsChange: (next: string[]) => void
  onSkillsChange: (next: string[]) => void
  onCertificationsChange: (next: string[]) => void
  onProficiencyLevelsChange: (next: string[]) => void
  // New profile-sourced filters (Branch, Worker Location, Procurement Source).
  // Each option is a code string; display-name resolution is a follow-up.
  branchOptions: string[]
  workerLocationOptions: string[]
  procurementSourceOptions: string[]
  selectedBranches: string[]
  selectedWorkerLocations: string[]
  selectedProcurementSources: string[]
  onBranchesChange: (next: string[]) => void
  onWorkerLocationsChange: (next: string[]) => void
  onProcurementSourcesChange: (next: string[]) => void
  /** Functional manager filter — option keys are stringified UserProfileIds (the manager's
   *  own profile id), labels are resolved by `resolveFunctionalManagerLabel`. Selection
   *  state stores raw IDs so worker-side filtering doesn't need name lookups at all. */
  functionalManagerOptions: string[]
  selectedFunctionalManagers: string[]
  onFunctionalManagersChange: (next: string[]) => void
  /** Engagement filter — option keys are engagement recordIds, labels resolved via
   *  `resolveEngagementLabel`. Selecting an engagement returns its whole team. */
  engagementOptions: string[]
  selectedEngagements: string[]
  onEngagementsChange: (next: string[]) => void
  resolveEngagementLabel?: (id: string) => string
  /** Optional code → display-name resolvers. The selection state still stores raw codes
   *  (so filter logic against the worker's stored codes is unaffected); these resolvers
   *  only change what the user sees in the option list, search, and pill values. */
  resolveBranchLabel?: (code: string) => string
  resolveWorkerLocationLabel?: (code: string) => string
  resolveProcurementSourceLabel?: (code: string) => string
  resolveFunctionalManagerLabel?: (idStr: string) => string
  /** Skill/certification option keys are stringified catalog SkillId/CertificationId;
   *  labels resolve via these. */
  resolveSkillLabel?: (idStr: string) => string
  resolveCertificationLabel?: (idStr: string) => string
  resolveProficiencyLevelLabel?: (idStr: string) => string
}

/**
 * Slide-in filter pane for the Resource Availability dashboard. Triggered by a small
 * "Filters" button with an active-count badge. All filtering happens client-side over
 * the cached payload, so applying a filter is instant — no network request fires.
 */
export function ResourceAvailabilityFilterPane(props: FilterPaneProps) {
  const { dictionary } = useLanguage()
  const t = dictionary?.resourceAvailability?.filterPane
  const tBar = dictionary?.resourceAvailability?.filterBar
  const [open, setOpen] = useState(false)

  const totalActive =
    props.selectedWorkerTypes.length +
    props.selectedJobFunctions.length +
    props.selectedSkills.length +
    props.selectedCertifications.length +
    props.selectedProficiencyLevels.length +
    props.selectedBranches.length +
    props.selectedWorkerLocations.length +
    props.selectedProcurementSources.length +
    props.selectedFunctionalManagers.length +
    props.selectedEngagements.length

  const resetAll = () => {
    props.onWorkerTypesChange([])
    props.onJobFunctionsChange([])
    props.onSkillsChange([])
    props.onCertificationsChange([])
    props.onProficiencyLevelsChange([])
    props.onBranchesChange([])
    props.onWorkerLocationsChange([])
    props.onProcurementSourcesChange([])
    props.onFunctionalManagersChange([])
    props.onEngagementsChange([])
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          aria-label={t?.triggerLabel || 'Open filters'}
          data-testid={testId('resource-availability-filters-trigger')}
        >
          <CustomFilterIcon size={14} className="mr-1.5" />
          {t?.triggerLabel || 'Filters'}
          {totalActive > 0 && (
            <span
              className="ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-4 rounded-full bg-blue-100 text-blue-700 px-1 text-[10px] font-semibold leading-none"
              aria-label={(t?.activeCountAria || '{n} active filters').replace('{n}', String(totalActive))}
            >
              {totalActive}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col gap-0 p-0">
        <SheetHeader className="px-5 pt-5 pb-3 border-b">
          {/* Right padding reserves space for the Sheet's auto-rendered close X (absolute
              at right-4 top-4) so "Reset all" doesn't slide under it. */}
          <div className="flex items-center justify-between gap-2 pr-8">
            <SheetTitle className="text-base">{t?.title || 'Filters'}</SheetTitle>
            {totalActive > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={resetAll}
              >
                {t?.resetAll || 'Reset all'}
              </Button>
            )}
          </div>
          <SheetDescription className="sr-only">
            {t?.description || 'Filter the dashboard by worker attributes. Filters apply immediately.'}
          </SheetDescription>
        </SheetHeader>

        <div className="overflow-y-auto px-5 py-4 space-y-5">
          {/* Engagement filter — headline of GW-153. Selecting an engagement returns its
              whole team. Placed first since it's the primary way users scope the board. */}
          <CheckboxFilterSection
            sectionId="engagement"
            title={t?.engagementSection || 'Engagement'}
            options={props.engagementOptions}
            selected={props.selectedEngagements}
            onChange={props.onEngagementsChange}
            emptyHint={t?.emptyEngagements || 'No engagements in current data'}
            resolveLabel={props.resolveEngagementLabel}
          />

          {/* Existing filters */}
          <CheckboxFilterSection
            sectionId="worker-type"
            title={t?.workerTypeSection || (tBar?.workerType || 'Worker Type')}
            options={props.workerTypeOptions}
            selected={props.selectedWorkerTypes}
            onChange={props.onWorkerTypesChange}
            emptyHint={tBar?.emptyWorkerTypes || 'No worker types in current data'}
          />
          <CheckboxFilterSection
            sectionId="job-function"
            title={t?.jobFunctionSection || (tBar?.jobFunction || 'Job Function')}
            options={props.jobFunctionOptions}
            selected={props.selectedJobFunctions}
            onChange={props.onJobFunctionsChange}
            emptyHint={tBar?.emptyJobFunctions || 'No job functions in current data'}
          />
          <CheckboxFilterSection
            sectionId="skills"
            title={t?.skillsSection || (tBar?.skills || 'Skills')}
            options={props.skillOptions}
            selected={props.selectedSkills}
            onChange={props.onSkillsChange}
            emptyHint={tBar?.emptySkills || 'No skills in current data'}
            resolveLabel={props.resolveSkillLabel}
          />
          <CheckboxFilterSection
            sectionId="certifications"
            title={t?.certificationsSection || (tBar?.certifications || 'Certifications')}
            options={props.certificationOptions}
            selected={props.selectedCertifications}
            onChange={props.onCertificationsChange}
            emptyHint={tBar?.emptyCertifications || 'No certifications in current data'}
            resolveLabel={props.resolveCertificationLabel}
          />
          <CheckboxFilterSection
            sectionId="proficiency-level"
            title={t?.proficiencyLevelsSection || (tBar?.proficiencyLevels || 'Proficiency Level')}
            options={props.proficiencyLevelOptions}
            selected={props.selectedProficiencyLevels}
            onChange={props.onProficiencyLevelsChange}
            emptyHint={tBar?.emptyProficiencyLevels || 'No proficiency levels in current data'}
            resolveLabel={props.resolveProficiencyLevelLabel}
          />

          {/* New filters — sourced from UserProfileWorker.EngagementDetails / T4SalariedDetails. */}
          <CheckboxFilterSection
            sectionId="branch"
            title={t?.branchSection || 'Branch'}
            options={props.branchOptions}
            selected={props.selectedBranches}
            onChange={props.onBranchesChange}
            emptyHint={t?.emptyBranches || 'No branches in current data'}
            resolveLabel={props.resolveBranchLabel}
          />
          <CheckboxFilterSection
            sectionId="worker-location"
            title={t?.workerLocationSection || 'Worker location'}
            options={props.workerLocationOptions}
            selected={props.selectedWorkerLocations}
            onChange={props.onWorkerLocationsChange}
            emptyHint={t?.emptyWorkerLocations || 'No worker locations in current data'}
            resolveLabel={props.resolveWorkerLocationLabel}
          />
          <CheckboxFilterSection
            sectionId="procurement-source"
            title={t?.procurementSourceSection || 'Procurement source'}
            options={props.procurementSourceOptions}
            selected={props.selectedProcurementSources}
            onChange={props.onProcurementSourcesChange}
            emptyHint={t?.emptyProcurementSources || 'No procurement sources in current data'}
            resolveLabel={props.resolveProcurementSourceLabel}
          />
          <CheckboxFilterSection
            sectionId="functional-manager"
            title={t?.functionalManagerSection || 'Functional manager'}
            options={props.functionalManagerOptions}
            selected={props.selectedFunctionalManagers}
            onChange={props.onFunctionalManagersChange}
            emptyHint={t?.emptyFunctionalManagers || 'No functional managers in current data'}
            resolveLabel={props.resolveFunctionalManagerLabel}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}

interface CheckboxFilterSectionProps {
  title: string
  options: string[]
  selected: string[]
  onChange: (next: string[]) => void
  emptyHint: string
  /** When provided, the option's visible label uses the resolved name; option keys remain
   *  the raw code so selection / filter logic is unaffected. Search matches both. */
  resolveLabel?: (option: string) => string
  /** Kebab-case identifier for this section, namespacing each option's data-testid. */
  sectionId: string
}

// Minimum option count above which the section gets a search input. Below this, the list
// fits in the scroll container without ever overflowing, so a search input would just be
// chrome for no benefit.
const SEARCH_THRESHOLD = 8

function CheckboxFilterSection({ title, options, selected, onChange, emptyHint, resolveLabel, sectionId }: CheckboxFilterSectionProps) {
  const { dictionary } = useLanguage()
  const t = dictionary?.resourceAvailability?.filterPane
  const [needle, setNeedle] = useState('')
  const selectedSet = new Set(selected)
  const toggle = (value: string) => {
    onChange(selectedSet.has(value) ? selected.filter(v => v !== value) : [...selected, value])
  }
  const isActive = selected.length > 0
  const showSearch = options.length >= SEARCH_THRESHOLD

  const labelFor = (opt: string) => {
    const resolved = resolveLabel?.(opt)
    return resolved && resolved.trim().length > 0 ? resolved : opt
  }

  const visibleOptions = useMemo(() => {
    const q = needle.trim().toLowerCase()
    if (!q) return options
    // Match both the raw code and the resolved label so users can search either form.
    return options.filter(o =>
      o.toLowerCase().includes(q) || labelFor(o).toLowerCase().includes(q)
    )
    // labelFor depends on resolveLabel; reading it via closure keeps the dep list small.
  }, [options, needle, resolveLabel])

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
          {title}
          {isActive && (
            <span className="ml-1.5 inline-flex items-center justify-center min-w-[1rem] h-4 rounded-full bg-blue-100 text-blue-700 px-1 text-[9px] font-semibold leading-none normal-case tracking-normal">
              {selected.length}
            </span>
          )}
        </h3>
        {isActive && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-[11px] text-muted-foreground hover:text-foreground"
          >
            {t?.clearSection || 'Clear'}
          </button>
        )}
      </div>
      {options.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic">{emptyHint}</div>
      ) : (
        <>
          {showSearch && (
            <div className="relative mb-1.5">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
              <Input
                type="text"
                value={needle}
                onChange={(e) => setNeedle(e.target.value)}
                placeholder={(t?.sectionSearchPlaceholder || 'Search {title}').replace('{title}', title.toLowerCase())}
                aria-label={(t?.sectionSearchAria || 'Search {title} options').replace('{title}', title)}
                className="h-7 pl-6 pr-6 text-[11px] font-normal text-foreground"
              />
              {needle && (
                <button
                  type="button"
                  onClick={() => setNeedle('')}
                  aria-label={t?.clearSectionSearchAria || 'Clear search'}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted-foreground/10 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
          {visibleOptions.length === 0 ? (
            <div className="text-[11px] text-muted-foreground italic px-1.5">
              {t?.sectionNoMatches || 'No matches'}
            </div>
          ) : (
            <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
              {visibleOptions.map(opt => {
                const checked = selectedSet.has(opt)
                const label = labelFor(opt)
                const showCodeAlso = label !== opt
                return (
                  <label
                    key={opt}
                    className={cn(
                      'flex items-center gap-2 cursor-pointer text-xs rounded px-1.5 py-1 font-normal',
                      'hover:bg-muted'
                    )}
                    title={showCodeAlso ? `${label} (${opt})` : label}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggle(opt)}
                      data-testid={testId('resource-availability-filter', sectionId, opt)}
                    />
                    <span className="truncate">{label}</span>
                  </label>
                )
              })}
            </div>
          )}
        </>
      )}
    </section>
  )
}
